// Status buckets for the Team Reports charts, and the drill-down they feed.
//
// ⚠️ WHY THIS IS A MODULE AND NOT TWO INLINE useMemos. The chart axis wants a
// READABLE label ("in progress") while the rows carry the RAW value
// ("in_progress"). Both charts built the label and threw the raw value away,
// so a drill-down that filtered on what the user clicked compared "in
// progress" against "in_progress" and silently rendered an empty table — the
// exact dead end the drill-down exists to remove, and indistinguishable from
// "there are none". Every bucket now carries both, and the invariant is
// tested rather than re-derived by hand in each chart.

export interface StatusBucket {
  /** Readable, for the chart axis. */
  status: string;
  /** The RAW value the rows carry. Filter on this, never on `status`. */
  key: string;
  count: number;
}

/** Underscores to spaces, first letter capitalised. */
export const statusLabel = (s: string): string =>
  s ? s.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : 'Unknown';

/**
 * Count rows by status.
 *
 * `order` pins the bars to a fixed sequence and INCLUDES EMPTY BUCKETS, so a
 * chart does not reshuffle as work moves between states. Without it, buckets
 * come back busiest-first and only for statuses actually present.
 */
export function bucketByStatus<T extends { status: string }>(
  rows: readonly T[],
  order?: readonly string[],
): StatusBucket[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const k = r.status;
    if (!k) continue;
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  if (order) {
    return order.map((key) => ({ key, status: key.replace(/_/g, ' '), count: counts.get(key) || 0 }));
  }
  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, status: key.replace(/_/g, ' '), count }))
    .sort((a, b) => b.count - a.count);
}

/** The rows behind one bucket. Keyed on `key` — see the note at the top. */
export function rowsForBucket<T extends { status: string }>(
  rows: readonly T[],
  key: string | null,
): T[] {
  if (!key) return [];
  return rows.filter((r) => r.status === key);
}
